import { Route } from '@/Enums/Routes/Route.enum';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import ShareModal from '@/components/Modal/ShareModal';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { deleteSharedTarget, shareTarget } from '@/store/api/shares.api';
import { useAppSelector } from '@/store/hooks/index';
import {
  selectForecastTablePagination,
  selectForecastTableRowCount,
} from '@/store/selectors/forecaster/forecasterTable.selectors';
import { afterDeleteForecast, deleteForecastTableItem, setFilters } from '@/store/slices/forecasterSlice';
import { SharesTargets } from '@/types/shares.types';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import NoRowsOverlay from './NoRowsOverlay';
import ToolBar from './Toolbar';
import getColumns from './columns';

const DataGrid = ({ data, loading }) => {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rowCount = useAppSelector(selectForecastTableRowCount);
  const paginationModel = useAppSelector(selectForecastTablePagination);

  const [isShareModalOpened, setShareModalOpened] = useState(false);
  const [shareTargetId, setShareTargetId] = useState(null);

  const setPagination = useCallback(pagination => dispatch(setFilters({ ...pagination })), [dispatch]);

  const handlePaginationModelChange = useCallback(
    newPaginationModel => {
      setPagination({ page: newPaginationModel.page + 1, limit: newPaginationModel.pageSize });
    },
    [setPagination],
  );

  const loadForecast = useCallback(
    (id, currentChannel) => {
      navigate({
        pathname: Route.FORECASTER,
        search: createSearchParams({ id, currentChannel }).toString(),
      });
    },
    [navigate],
  );

  const onDeleteForecasterItem = useCallback(
    async row => {
      if (row.shares) {
        try {
          await deleteSharedTarget(row.shares.id);
          dispatch(afterDeleteForecast(row.id));
        } catch (e) {
          console.error(e);
        }
      } else {
        dispatch(deleteForecastTableItem({ id: row.id }));
      }
    },
    [dispatch],
  );

  const onShareForecasterItem = useCallback(id => {
    setShareModalOpened(true);
    setShareTargetId(id);
  }, []);

  const onShareModalClose = useCallback(() => {
    setShareModalOpened(false);
    setShareTargetId(null);
  }, []);

  const onSubmitShare = useCallback(
    async ({ receivingUserIds, shareWithOrg, targetId }) => {
      try {
        const res = await shareTarget({
          shareWithOrg,
          targetId,
          receivingUserIds,
          targetTable: SharesTargets.FORECASTS,
        });

        if (res) {
          toast.success('Forecast successfully shared.');
        }
      } catch (e) {
        console.error(e);
        toast.error('Something went wrong on sharing attempt.');
      } finally {
        onShareModalClose();
      }
    },
    [shareTarget, onShareModalClose],
  );

  const columns = useMemo(
    () => getColumns(loadForecast, onShareForecasterItem, onDeleteForecasterItem),
    [loadForecast, onShareForecasterItem, onDeleteForecasterItem],
  );

  return (
    <>
      <Grid
        apiRef={apiRef}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode='server'
        rowCount={rowCount}
        rows={data}
        columns={columns}
        columnHeaderHeight={30}
        initialState={{
          columns: {
            columnVisibilityModel: {
              geo_fence: false,
              geo_exclusion: false,
            },
          },
        }}
        getRowId={row => (row.shared ? `s_${row.id}` : row.id)}
        pagination
        slots={{
          pagination: ServerSidePagination,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
          noRowsOverlay: NoRowsOverlay,
          toolbar: ToolBar,
        }}
        slotProps={{
          pagination: { loading, rowCount },
          footer: { className: 'relative' },
        }}
      />

      <ShareModal
        open={isShareModalOpened}
        onModalClose={onShareModalClose}
        onSubmit={onSubmitShare}
        targetId={shareTargetId}
        targetTable={SharesTargets.FORECASTS}
      />
    </>
  );
};

DataGrid.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};

export default DataGrid;
